import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { type AuthUser } from "wasp/auth";
import {
  getPendingApprovals,
  getApprovalHistory,
  getApprovalStats,
  approveContent,
  rejectContent,
} from "wasp/client/operations";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Users,
  BarChart3,
  Eye,
  MessageSquare,
  User,
  Search,
} from "lucide-react";
import DefaultLayout from "../layout/DefaultLayout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  AdminFlyoutPane,
  FlyoutSection,
  FlyoutField,
  FlyoutActions,
} from "../components/AdminFlyoutPane";
import { TableFilterLayout } from "../../components/ui/table-filter-layout";
interface ApprovalRequest {
  id: string;
  contentId: string;
  contentType: string;
  locationId: string;
  authorId: string;
  requestedAt: string;
  status: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  showRejectForm?: boolean;
  content: {
    id: string;
    title: string;
    description?: string;
    contentBody?: string;
    author: {
      id: string;
      email: string;
      username: string;
    };
  };
  author: {
    id: string;
    email: string;
    username: string;
  };
  reviewer?: {
    id: string;
    email: string;
    username: string;
  };
}

interface ApprovalStats {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

const CONTENT_TYPES = ["FACILITY", "NOTICE", "TENANT", "EVENT"];
const REJECTION_REASONS = [
  "INACCURATE_INFO",
  "INAPPROPRIATE_CONTENT",
  "DUPLICATE",
  "POLICY_VIOLATION",
  "INCOMPLETE_INFO",
  "OTHER",
];

export default function ApprovalDashboardPage({ user }: { user: AuthUser }) {
  const { selectedLocation } = useLocationContext();
  const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([
    "FACILITY",
    "NOTICE",
    "TENANT",
    "EVENT",
  ]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedRequest, setSelectedRequest] =
    useState<ApprovalRequest | null>(null);
  const [reviewNotes, setReviewNotes] = useState<string>("");
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [rejectionNotes, setRejectionNotes] = useState<string>("");
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [currentRequestIndex, setCurrentRequestIndex] = useState<number>(-1);

  const toggleContentType = (type: string) => {
    setSelectedContentTypes((prev) => {
      if (prev.includes(type)) {
        // Don't allow deselecting all types
        if (prev.length === 1) return prev;
        return prev.filter((t) => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Get all pending approvals for all content types
  const {
    data: allPendingApprovals,
    isLoading: loadingPending,
    refetch: refetchPending,
  } = useQuery(
    getPendingApprovals,
    {
      locationId: selectedLocation?.id || "",
      contentType: undefined as any, // Get all types
    },
    {
      enabled: !!selectedLocation?.id,
    },
  );

  // Get all approval history for all content types
  const {
    data: allApprovalHistory,
    isLoading: loadingHistory,
    refetch: refetchHistory,
  } = useQuery(
    getApprovalHistory,
    {
      locationId: selectedLocation?.id || "",
      contentType: undefined as any, // Get all types
    },
    {
      enabled: !!selectedLocation?.id,
    },
  );

  // Filter results based on selected content types and search
  const searchLower = (searchTerm || "").toLowerCase();
  const matchSearch = (item: any) => {
    if (!searchLower) return true;
    const title = item.content?.title || "";
    const author = item.author?.username || item.author?.email || "";
    return (
      title.toLowerCase().includes(searchLower) ||
      author.toLowerCase().includes(searchLower)
    );
  };

  const pendingApprovals = (allPendingApprovals || [])
    .filter((item: any) => selectedContentTypes.includes(item.contentType))
    .filter(matchSearch);

  const approvalHistory = (allApprovalHistory || [])
    .filter((item: any) => selectedContentTypes.includes(item.contentType))
    .filter(matchSearch);

  const approveContentAction = useAction(approveContent);
  const rejectContentAction = useAction(rejectContent);

  const handleApprove = async (requestId: string) => {
    setIsApproving(true);
    try {
      await approveContentAction({
        contentId: requestId,
        reviewNotes: reviewNotes || undefined,
      });

      setReviewNotes("");
      setSelectedRequest(null);
      refetchPending();
      refetchHistory();
      alert("Content approved successfully");
    } catch (error) {
      console.error("Error approving content:", error);
      alert("Failed to approve content");
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (requestId: string) => {
    if (!rejectionReason) {
      alert("Please select a rejection reason");
      return;
    }

    setIsRejecting(true);
    try {
      await rejectContentAction({
        contentId: requestId,
        rejectionReason: rejectionReason as any,
        rejectionNotes: rejectionNotes || undefined,
      });

      setRejectionReason("");
      setRejectionNotes("");
      setSelectedRequest(null);
      refetchPending();
      refetchHistory();
      alert("Content rejected successfully");
    } catch (error) {
      console.error("Error rejecting content:", error);
      alert("Failed to reject content");
    } finally {
      setIsRejecting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "PENDING":
        return (
          <span className="text-sm font-medium text-secondary flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Pending
          </span>
        );
      case "APPROVED":
        return (
          <span className="text-sm font-medium text-green-600 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Approved
          </span>
        );
      case "REJECTED":
        return (
          <span className="text-sm font-medium text-destructive flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="text-sm font-medium text-muted-foreground">
            {status}
          </span>
        );
    }
  };

  // Navigation handlers
  const handleNavigatePrevious = () => {
    if (currentRequestIndex > 0) {
      const newIndex = currentRequestIndex - 1;
      setCurrentRequestIndex(newIndex);
      setSelectedRequest(pendingApprovals[newIndex]);
      setReviewNotes("");
      setRejectionReason("");
      setRejectionNotes("");
    }
  };

  const handleNavigateNext = () => {
    if (currentRequestIndex < pendingApprovals.length - 1) {
      const newIndex = currentRequestIndex + 1;
      setCurrentRequestIndex(newIndex);
      setSelectedRequest(pendingApprovals[newIndex]);
      setReviewNotes("");
      setRejectionReason("");
      setRejectionNotes("");
    }
  };

  const hasPrevious = currentRequestIndex > 0;
  const hasNext = currentRequestIndex < pendingApprovals.length - 1;

  return (
    <DefaultLayout user={user}>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-20 bg-white dark:bg-sidebar-active transition-colors">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Approval Dashboard
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Review and approve content submissions
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter is rendered within each tab to align with table columns */}

        {/* Scrollable Content */}
        <div className="pt-[calc(var(--header-height)+2rem)]">
          <div className="pb-4">
            {/* Removed top content type toggle to avoid duplication; filters are in table header */}

            {/* Main Content */}
            <Tabs defaultValue="pending" className="space-y-4">
              <TabsList className="ml-4">
                <TabsTrigger value="pending">Pending Approvals</TabsTrigger>
                <TabsTrigger value="history">Approval History</TabsTrigger>
              </TabsList>

              <TabsContent value="pending" className="space-y-4">
                {!selectedLocation ? (
                  <Alert>
                    <AlertDescription>
                      Please select a location from the top bar to view pending
                      approvals.
                    </AlertDescription>
                  </Alert>
                ) : loadingPending ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="w-full">
                    {/* Filter + Header aligned to columns */}
                    <TableFilterLayout
                      columns={[
                        { title: "Title", span: 3 },
                        { title: "Type", span: 2 },
                        { title: "Author", span: 2 },
                        { title: "Requested", span: 2 },
                        { title: "Description", span: 2 },
                      ]}
                      filters={[
                        {
                          span: 3,
                          content: (
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                              <input
                                type="text"
                                placeholder="Search title or author..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          ),
                        },
                        {
                          span: 2,
                          content: (
                            <select
                              value={typeFilter}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTypeFilter(val);
                                setSelectedContentTypes(
                                  val === "all"
                                    ? ["FACILITY", "NOTICE", "TENANT", "EVENT"]
                                    : [val],
                                );
                              }}
                              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="all">All types</option>
                              {CONTENT_TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          ),
                        },
                        { span: 2, content: <div className="h-10" /> },
                        { span: 2, content: <div className="h-10" /> },
                        { span: 2, content: <div className="h-10" /> },
                      ]}
                    />

                    {/* Table Body */}
                    <div className="divide-y divide-gray-200">
                      {pendingApprovals.length > 0 ? (
                        pendingApprovals.map((request: any) => (
                          <div
                            key={request.id}
                            className="admin-hoverable py-3 transition-colors cursor-pointer"
                            onClick={() => {
                              setSelectedRequest(request);
                              setCurrentRequestIndex(
                                pendingApprovals.indexOf(request),
                              );
                            }}
                          >
                            <div className="grid grid-cols-12 gap-4 items-start px-6">
                              <div className="col-span-3 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {request.content.title}
                                </div>
                              </div>
                              <div className="col-span-2 min-w-0">
                                <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                  {request.contentType}
                                </div>
                              </div>
                              <div className="col-span-2">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  {request.author.username ||
                                    request.author.email}
                                </div>
                              </div>
                              <div className="col-span-2">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  {formatDate(request.requestedAt)}
                                </div>
                              </div>
                              <div className="col-span-2">
                                {request.content.description && (
                                  <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                    {request.content.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-8 px-6">
                          <div className="text-center text-sm text-gray-500">
                            No pending approvals found.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="history" className="space-y-4">
                {!selectedLocation ? (
                  <Alert>
                    <AlertDescription>
                      Please select a location from the top bar to view approval
                      history.
                    </AlertDescription>
                  </Alert>
                ) : loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="w-full">
                    {/* Filter + Header aligned to columns */}
                    <TableFilterLayout
                      columns={[
                        { title: "Title", span: 3 },
                        { title: "Type", span: 1 },
                        { title: "Author", span: 2 },
                        { title: "Reviewed", span: 2 },
                        { title: "Reviewer", span: 2 },
                        { title: "Status", span: 1 },
                        { title: "Notes", span: 1 },
                      ]}
                      filters={[
                        {
                          span: 3,
                          content: (
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                              <input
                                type="text"
                                placeholder="Search title or author..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          ),
                        },
                        {
                          span: 1,
                          content: (
                            <select
                              value={typeFilter}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTypeFilter(val);
                                setSelectedContentTypes(
                                  val === "all"
                                    ? ["FACILITY", "NOTICE", "TENANT", "EVENT"]
                                    : [val],
                                );
                              }}
                              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="all">All types</option>
                              {CONTENT_TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          ),
                        },
                        { span: 7, content: <div className="h-10" /> },
                        {
                          span: 1,
                          content: (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-10 w-full"
                              onClick={() => {
                                setSearchTerm("");
                                setTypeFilter("all");
                                setSelectedContentTypes([
                                  "FACILITY",
                                  "NOTICE",
                                  "TENANT",
                                  "EVENT",
                                ]);
                              }}
                            >
                              Reset
                            </Button>
                          ),
                        },
                      ]}
                    />

                    {/* Table Body */}
                    <div className="divide-y divide-gray-200">
                      {approvalHistory.length > 0 ? (
                        approvalHistory.map((request: any) => (
                          <div
                            key={request.id}
                            className="admin-hoverable py-3 transition-colors"
                          >
                            <div className="grid grid-cols-12 gap-4 items-start px-6">
                              <div className="col-span-3 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {request.content.title}
                                </div>
                              </div>
                              <div className="col-span-1 min-w-0">
                                <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                  {request.contentType}
                                </div>
                              </div>
                              <div className="col-span-2">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  {request.author.username ||
                                    request.author.email}
                                </div>
                              </div>
                              <div className="col-span-2">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  {request.reviewedAt
                                    ? formatDate(request.reviewedAt)
                                    : "-"}
                                </div>
                              </div>
                              <div className="col-span-2">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  {request.reviewer?.username ||
                                    request.reviewer?.email ||
                                    "-"}
                                </div>
                              </div>
                              <div className="col-span-1">
                                {getStatusText(request.status)}
                              </div>
                              <div className="col-span-1">
                                {request.reviewNotes && (
                                  <MessageSquare className="h-4 w-4 text-gray-600 inline" />
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-8 px-6">
                          <div className="text-center text-sm text-gray-500">
                            No approval history found.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Review Flyout */}
        <AdminFlyoutPane
          open={!!selectedRequest}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedRequest(null);
              setCurrentRequestIndex(-1);
              setReviewNotes("");
              setRejectionReason("");
              setRejectionNotes("");
            }
          }}
          title="Review Approval Request"
          width="2xl"
          mode="view"
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          headerActions={
            selectedRequest && !selectedRequest.showRejectForm ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() =>
                    setSelectedRequest({
                      ...selectedRequest,
                      showRejectForm: true,
                    })
                  }
                  className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                >
                  Reject
                </Button>

                <Button
                  onClick={() => handleApprove(selectedRequest.contentId)}
                  disabled={isApproving}
                  size="sm"
                  className="w-[110px]"
                >
                  {isApproving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    "Approve"
                  )}
                </Button>
              </>
            ) : selectedRequest?.showRejectForm ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setSelectedRequest({
                      ...selectedRequest,
                      showRejectForm: false,
                    })
                  }
                  className="w-[110px]"
                >
                  Close
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleReject(selectedRequest.contentId)}
                  disabled={isRejecting || !rejectionReason}
                  className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                >
                  {isRejecting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin text-white" />
                      <span className="text-white">Rejecting...</span>
                    </>
                  ) : (
                    "Confirm"
                  )}
                </Button>
              </>
            ) : undefined
          }
        >
          {selectedRequest && (
            <div className="border border-gray-200 dark:border-gray-700">
              <div className="grid" style={{ gridTemplateColumns: "30% 70%" }}>
                {/* Content Information Section */}
                <div className="col-span-2 border-b border-gray-200 dark:border-gray-700 py-3 px-4 bg-gray-50 dark:bg-gray-800">
                  <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    CONTENT INFORMATION
                  </h3>
                </div>

                <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Title
                  </div>
                </div>
                <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {selectedRequest.content.title}
                  </div>
                </div>

                <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Content Type
                  </div>
                </div>
                <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm">
                    <Badge variant="outline">
                      {selectedRequest.contentType}
                    </Badge>
                  </div>
                </div>

                <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Author
                  </div>
                </div>
                <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {selectedRequest.author.username ||
                      selectedRequest.author.email}
                  </div>
                </div>

                <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Requested Date
                  </div>
                </div>
                <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {formatDate(selectedRequest.requestedAt)}
                  </div>
                </div>

                {/* Content Details Section */}
                {(selectedRequest.content.description ||
                  selectedRequest.content.contentBody) && (
                  <>
                    <div className="col-span-2 border-b border-gray-200 dark:border-gray-700 py-3 px-4 bg-gray-50 dark:bg-gray-800">
                      <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                        CONTENT DETAILS
                      </h3>
                    </div>

                    {selectedRequest.content.description && (
                      <>
                        <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            Description
                          </div>
                        </div>
                        <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {selectedRequest.content.description}
                          </div>
                        </div>
                      </>
                    )}

                    {selectedRequest.content.contentBody && (
                      <>
                        <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            Content Body
                          </div>
                        </div>
                        <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                          <div className="text-sm text-gray-900 dark:text-white">
                            <div className="p-3 bg-gray-50 dark:bg-gray-800">
                              {selectedRequest.content.contentBody}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Review Section */}
                <div className="col-span-2 border-b border-gray-200 dark:border-gray-700 py-3 px-4 bg-gray-50 dark:bg-gray-800">
                  <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    REVIEW
                  </h3>
                </div>

                <div className="border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Review Notes
                  </div>
                </div>
                <div className="py-3 px-4">
                  <div>
                    <Textarea
                      id="reviewNotes"
                      placeholder="Add any notes about your decision..."
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      rows={3}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedRequest && selectedRequest.showRejectForm && (
            <div className="mt-6 border border-gray-200 dark:border-gray-700">
              <div className="grid" style={{ gridTemplateColumns: "30% 70%" }}>
                <div className="col-span-2 border-b border-gray-200 dark:border-gray-700 py-3 px-4 bg-gray-50 dark:bg-gray-800">
                  <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    REJECTION DETAILS
                  </h3>
                </div>

                <div className="border-b border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Rejection Reason *
                  </div>
                </div>
                <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                  <Select
                    value={rejectionReason}
                    onValueChange={setRejectionReason}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {REJECTION_REASONS.map((reason) => (
                        <SelectItem key={reason} value={reason}>
                          {reason.replace("_", " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-r border-gray-200 dark:border-gray-700 py-3 px-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Rejection Notes
                  </div>
                </div>
                <div className="py-3 px-4">
                  <Textarea
                    id="rejectionNotes"
                    placeholder="Explain why the content was rejected..."
                    value={rejectionNotes}
                    onChange={(e) => setRejectionNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}
        </AdminFlyoutPane>
      </div>
    </DefaultLayout>
  );
}
